import React from 'react';

import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { TimeInput, DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import useCreateStaffAvailabilityException from 'hooks/staffAvailabilityException/useCreateStaffAvailabilityException';
import notificationHandler from 'utils/notifications';

interface CreateWeekDayStaffAvailabilityProps {
  opened: boolean;
  handleClose: () => void;
}

interface FormValues {
  date: null;
  exceptions: {
    startTime: string | null;
    endTime: string | null;
  }[];
}

const CreateStaffAvailabilityExceptionModal = ({
  opened,
  handleClose,
}: CreateWeekDayStaffAvailabilityProps): JSX.Element => {
  const theme = useMantineTheme();
  const createMutation = useCreateStaffAvailabilityException();
  const { successNotification, errorNotification } = notificationHandler();

  const form = useForm<FormValues>({
    initialValues: {
      date: null,
      exceptions: [],
    },

    validate: values => ({
      date: values.date ? null : 'Date is Required',
    }),
  });

  const isUnavailableState = !form.values.exceptions.length;

  const handleCloseAndReset = (): void => {
    form.reset();
    handleClose();
  };

  const handleSave = (formValues: FormValues): void => {
    let formattedExceptions = [];
    if (isUnavailableState) {
      formattedExceptions = [
        {
          date: dayjs(formValues.date).format('YYYY-MM-DD'),
          startTime: null,
          endTime: null,
        },
      ];
    } else {
      formattedExceptions = formValues.exceptions.map(exception => ({
        ...exception,
        date: dayjs(formValues.date).format('YYYY-MM-DD'),
      }));
    }

    createMutation.mutate(
      {
        exceptions: formattedExceptions,
      },
      {
        onError: error => {
          errorNotification(
            'Availability exceptions create success failed',
            // @ts-ignore
            error.response?.data?.message || error.response?.data?.error,
          );
        },
        onSuccess: () => {
          successNotification('Availability exceptions create success');
          handleCloseAndReset();
        },
      },
    );
  };

  const addStaffAvailabilityException = (): void => {
    form.insertListItem('exceptions', {
      startTime: null,
      endTime: null,
    });
  };

  const removeStaffAvailabilityException = (index: number): void => {
    form.removeListItem('exceptions', index);
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={2}>Add Specific Slots</Title>}
      size='lg'
      overlayProps={{
        opacity: 0.55,
      }}
    >
      <form onSubmit={form.onSubmit(handleSave)}>
        <Stack p='xl'>
          <Text size='lg'>The slots included here will override your standard availability.</Text>

          <DatePickerInput
            label='Select Date'
            withAsterisk
            required
            minDate={new Date()}
            dropdownType='modal'
            {...form.getInputProps('date')}
          />

          <Text size='lg'>When are you available?</Text>

          {isUnavailableState && (
            <Group noWrap>
              <TextInput placeholder='Unavailable' disabled w='100%' />
              <ActionIcon size='xl' onClick={() => addStaffAvailabilityException()}>
                <IconPlus size={theme.fontSizes.xl} />
              </ActionIcon>
            </Group>
          )}

          {!isUnavailableState && (
            <Stack>
              {form.values.exceptions.map((_, index) => (
                <Group key={index} noWrap align='end'>
                  <TimeInput
                    label='From'
                    withAsterisk
                    required
                    w='100%'
                    {...form.getInputProps(`exceptions.${index}.startTime`)}
                  />
                  <TimeInput
                    label='To'
                    withAsterisk
                    required
                    w='100%'
                    {...form.getInputProps(`exceptions.${index}.endTime`)}
                  />
                  <Group spacing='xs' noWrap>
                    <ActionIcon size='xl' onClick={() => addStaffAvailabilityException()}>
                      <IconPlus size={theme.fontSizes.xl} />
                    </ActionIcon>
                    <ActionIcon size='xl' onClick={() => removeStaffAvailabilityException(index)}>
                      <IconTrash size={theme.fontSizes.xl} />
                    </ActionIcon>
                  </Group>
                </Group>
              ))}
            </Stack>
          )}

          <Group position='right' mt='xl'>
            <Button onClick={handleCloseAndReset} disabled={createMutation.isLoading} variant='subtle'>
              Cancel
            </Button>
            <Button type='submit' loading={createMutation.isLoading}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default CreateStaffAvailabilityExceptionModal;
